import React from 'react';
import { Alert, Card, Col, Descriptions, Divider, Row, Typography } from 'antd';
import { AppapproDTO } from '@/shared/api/model';
import { useProductPropertiesById } from '@/modules/loan-product-settings/hooks/useProductProperties';
import { formatCurrency, parseJavaDateArray } from '@/shared/utils/formatters';
import { useLoanApprovalData } from './LoanApprovalDataProvider';
import LoanScheduleComponent from './LoanScheduleComponent';
import { LoanFeeBreakdown } from './application/LoanFeeBreakdown';

interface LoanDetailsViewProps {
  data?: any;
  formikProps?: any;
}

const { Title, Text } = Typography;

const LoanDetailsView: React.FC<LoanDetailsViewProps> = ({ data, formikProps }) => {
  const values = formikProps?.values || data || {};
  const valueData = (values.valueData || {}) as AppapproDTO & Record<string, any>;
  const recommendedLoanAmount = valueData?.recommendedLoanAmt;
  const recommendedLoanPeriod = valueData?.recommendedLnPeriod;
  const effectiveLoanAmount = recommendedLoanAmount || valueData?.loanAmount;
  const effectiveLoanPeriod = recommendedLoanPeriod || valueData?.lnPeriod;
  const hasRecommendations = Boolean(recommendedLoanAmount || recommendedLoanPeriod);

  const modifiedAppapproDTO: AppapproDTO = {
    ...valueData,
    loanAmount: effectiveLoanAmount,
    lnPeriod: effectiveLoanPeriod,
  };

  const { loanBalanceData, isBalanceLoading } = useLoanApprovalData();
  const { data: productPropertiesResponse } = useProductPropertiesById(valueData?.glNo ?? undefined);
  const installmentRoundingPrecision = (productPropertiesResponse?.data as any)?.installmentRoundingPrecision ?? null;

  return (
    <Card bordered={false} className="loan-details-view">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Title level={4}>Loan Details Review</Title>
          <Text type="secondary">
            Review the loan information below. This view is read-only and does not allow updating loan values.
          </Text>
        </Col>

        <Col span={24}>
          <Alert
            message="Read-Only View"
            description="This step is intended for users who need to inspect loan details, fee impact, and repayment schedule without editing recommended values."
            type="info"
            showIcon
          />
        </Col>

        <Col span={24}>
          <Card title="Loan Details" size="small">
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Loan ID">{valueData?.loanId || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Customer">{valueData?.name || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Product">{(valueData as any)?.productName || valueData?.glNo || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Application Date">
                {valueData?.appliDate ? parseJavaDateArray(valueData.appliDate)?.toLocaleDateString() || 'N/A' : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Applied Amount">{formatCurrency(valueData?.loanAmount)}</Descriptions.Item>
              <Descriptions.Item label="Applied Period">
                {valueData?.lnPeriod ? `${valueData.lnPeriod} ${Number(valueData.lnPeriod) === 1 ? 'month' : 'months'}` : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Interest Rate">{valueData?.intRate ? `${valueData.intRate}%` : 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Branch">{valueData?.branch || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Employer">{(valueData as any)?.employerName || 'N/A'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Recommendation Summary" size="small">
            {hasRecommendations ? (
              <Descriptions bordered column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label="Original Loan Amount">{formatCurrency(valueData?.loanAmount)}</Descriptions.Item>
                <Descriptions.Item label="Recommended Loan Amount">{formatCurrency(recommendedLoanAmount)}</Descriptions.Item>
                <Descriptions.Item label="Original Loan Period">
                  {valueData?.lnPeriod ? `${valueData.lnPeriod} ${Number(valueData.lnPeriod) === 1 ? 'month' : 'months'}` : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Recommended Loan Period">
                  {recommendedLoanPeriod ? `${recommendedLoanPeriod} ${Number(recommendedLoanPeriod) === 1 ? 'month' : 'months'}` : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                message="No recommendation overrides"
                description="No recommended loan amount or tenure has been captured yet. The original application values are being used for the review below."
                type="warning"
                showIcon
              />
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Fee Breakdown & Net Amount" size="small">
            <Alert
              message="Fee Information"
              description={`This preview uses ${hasRecommendations ? 'the recommended' : 'the original'} loan amount and tenure. ${isBalanceLoading ? 'Loading loan balance...' : loanBalanceData ? 'Existing loan balance will be settled where applicable.' : ''}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <LoanFeeBreakdown
              glNo={valueData?.glNo}
              loanAmount={effectiveLoanAmount}
              loanPeriod={effectiveLoanPeriod}
              showTitle={false}
              compact={false}
              loanBalanceData={loanBalanceData}
              netAmountLabel="Net Amount Customer Will Receive"
            />
          </Card>
        </Col>

        <Col span={24}>
          <Divider orientation="left">Loan Repayment Schedule</Divider>
          <Alert
            message={hasRecommendations ? 'Recommended Schedule Preview' : 'Original Schedule Preview'}
            description={hasRecommendations
              ? `This schedule is based on the recommended loan amount of ${formatCurrency(effectiveLoanAmount)} and period of ${effectiveLoanPeriod} months.`
              : `This schedule is based on the original loan amount of ${formatCurrency(effectiveLoanAmount)} and period of ${effectiveLoanPeriod} months.`}
            type={hasRecommendations ? 'success' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
          />
          <LoanScheduleComponent appapproDTO={modifiedAppapproDTO} installmentRoundingPrecision={installmentRoundingPrecision} />
        </Col>
      </Row>
    </Card>
  );
};

export default LoanDetailsView;
